import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  Button,
  Skeleton,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CampaignIcon from "@mui/icons-material/CampaignOutlined";
import LiveTvOutlinedIcon from "@mui/icons-material/LiveTvOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";
import BoothIconPng from "../assets/icons/booth.png";
import EventIconPng from "../assets/icons/events.png";
import MapIconPng from "../assets/icons/map.png";
import ItemsIconPng from "../assets/icons/items.png";
import RecognitionIconPng from "../assets/icons/recognition.png";
import CharitiesIconPng from "../assets/icons/charities.png";
import SettingsIconPng from "../assets/icons/setting.png";
import PaymentIconPng from "../assets/icons/payment.png";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSpaceId } from "../hooks/useSpaceId";
import { usePermissions } from "../hooks/usePermissions";

import LocalText from "../components/common/LocalText";
import { useLocalText } from "../components/common/useLocalText";
// import { getRecentActivity } from "../api/activityApi";
import ActivityLogDialog from "../components/activity/ActivityLogDialog";


const actionMeta = {
  created: { Icon: CheckCircleOutlineIcon, color: "#4ade80", bg: "rgba(34,197,94,0.18)" },
  updated: { Icon: EditOutlinedIcon,       color: "#fb923c", bg: "rgba(251,146,60,0.18)" },
  deleted: { Icon: DeleteOutlineIcon,      color: "#f87171", bg: "rgba(248,113,113,0.18)" },
};

const getActionMeta = (action) => actionMeta[(action ?? "").toLowerCase()] ?? actionMeta.created;

// Ordered by priority — first 4 the user can access are shown as quick access cards.

const ALL_QUICK_ACCESS = [
  { label: "User Analytics", path: "/user-analytics", Icon: LeaderboardOutlinedIcon },
  { label: "Booths",       path: "/booth",        icon: BoothIconPng },
  { label: "Events",       path: "/events",        icon: EventIconPng },
  { label: "Charities",    path: "/charities",     icon: CharitiesIconPng },
  { label: "Hosting",      path: "/hosting",       Icon: LiveTvOutlinedIcon },
  { label: "Teleport Map", path: "/teleport-map",  icon: MapIconPng },
  { label: "Items",        path: "/items",         icon: ItemsIconPng },
  { label: "Recognition",  path: "/recognition",   icon: RecognitionIconPng },
  { label: "Advertisers",  path: "/advertisers",   Icon: CampaignIcon },
  { label: "Payments",     path: "/payments",      icon: PaymentIconPng },
  { label: "Admin Portal", path: "/admin",         Icon: AdminPanelSettingsOutlinedIcon },
  { label: "Settings",     path: "/settings",      icon: SettingsIconPng },
];

const Dashboard = () => {
  const { user } = useAuth();
  const spaceId = useSpaceId();
  const navigate = useNavigate();
  const { canAccessNav, isGlobalAdmin, isEntityAdmin, isGmsAccountant } = usePermissions();
  const isSystemAdmin = isGlobalAdmin || isEntityAdmin || isGmsAccountant;

  const quickAccessItems = ALL_QUICK_ACCESS.filter(item => canAccessNav(item.path)).slice(0, 4);
  const welcomePrefix = useLocalText("Welcome back,");
  const subtitleText = useLocalText("Here's what's happening in your CMS today.");
  const thereFallback = useLocalText("there");

  const [recentActivity, setRecentActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);

  // useEffect(() => {
  //   if (!spaceId || isSystemAdmin) return;
  //   setActivityLoading(true);
  //   getRecentActivity(6, 0)
  //     .then(res => setRecentActivity(Array.isArray(res.data) ? res.data : (res.data?.items ?? [])))
  //     .catch(() => setRecentActivity([]))
  //     .finally(() => setActivityLoading(false));
  // }, [spaceId, isSystemAdmin]);

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>

      {/* Welcome + Quick Access — merged container */}
      <Box
        sx={{
          mb: { xs: 3, md: 4 },
          px: { xs: 3, md: 5 },
          pt: { xs: 4, md: 5 },
          pb: { xs: 3, md: 4 },
          borderRadius: 4,
          background: "linear-gradient(135deg, rgba(10,50,109,0.90) 0%, rgba(83,131,179,0.80) 100%)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          border: "none",
          boxShadow: "0 8px 32px rgba(10,50,109,0.18), inset 0 1px 0 rgba(255,255,255,0.18)",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.10)",
            pointerEvents: "none",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: -50,
            left: -50,
            width: 160,
            height: 160,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.09)",
            pointerEvents: "none",
          },
        }}
      >
        {/* Medium + small companions trailing from top-right toward center */}
        <Box sx={{ position: "absolute", top: 85,  right: 120, width: 80, height: 80, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.09)", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", top: 145, right: 250, width: 42, height: 42, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.07)", pointerEvents: "none" }} />

        {/* Welcome text */}
        <Typography
          variant="h4"
          fontWeight={800}
          sx={{ color: "#ffffff", letterSpacing: "-0.5px", mb: 0.75, position: "relative", fontSize: { xs: "1.4rem", sm: "1.75rem", md: "2.125rem" } }}
        >
          {welcomePrefix} {user?.name ?? thereFallback}.
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.78)", fontWeight: 500, mb: 3, position: "relative" }}>
          {subtitleText}
        </Typography>

        {/* Divider */}
        <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.15)", mb: 3, position: "relative" }} />

        {/* Quick Access */}
        <Box sx={{ mb: 1.5, position: "relative" }}>
          <Typography variant="subtitle2" fontWeight={700} color="white">
            <LocalText>Quick Access</LocalText>
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.55)" }}>
            <LocalText>Frequently used tools</LocalText>
          </Typography>
        </Box>
        <Grid container spacing={2} sx={{ position: "relative" }}>
          {quickAccessItems.map((item, idx) => (
            <Grid key={idx} size={{ xs: 6, sm: 3 }}>
              <Box
                onClick={() => navigate(item.path)}
                sx={{
                  bgcolor: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.15)",
                  borderRadius: 3,
                  p: { xs: 2, md: 2.5 },
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1.5,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.22)",
                    borderColor: "rgba(255,255,255,0.35)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                {item.icon
                  ? <Box component="img" src={item.icon} alt={item.label} sx={{ width: { xs: 36, md: 42 }, height: { xs: 36, md: 42 } }} />
                  : <item.Icon sx={{ fontSize: { xs: 36, md: 42 }, color: "white" }} />
                }
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="white"
                  textAlign="center"
                  sx={{ lineHeight: 1.3, letterSpacing: 0.3 }}
                >
                  <LocalText>{item.label}</LocalText>
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Recent Activity — commented out, not needed for now */}
      {false && !isSystemAdmin && <Card
        elevation={0}
        sx={{
          bgcolor: "#0a326d",
          borderRadius: 4,
          p: { xs: 2.5, md: 3.5 },
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: 1,
            mb: 3,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} color="white">
              <LocalText>Recent Activity</LocalText>
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", display: "block", mt: 0.3 }}>
              <LocalText>Latest updates across your platform</LocalText>
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={() => setActivityDialogOpen(true)}
            sx={{
              color: "rgba(255,255,255,0.75)",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 2,
              px: 2,
              py: 0.6,
              fontSize: 12,
              textTransform: "none",
              fontWeight: 600,
              flexShrink: 0,
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.08)",
                borderColor: "rgba(255,255,255,0.4)",
                color: "white",
              },
            }}
          >
            <LocalText>View All Updates</LocalText>
          </Button>
        </Box>

        <Grid container spacing={2}>
          {activityLoading ? (
            [0, 1, 2, 3, 4].map(i => (
              <Grid key={i} size={{ xs: 12, md: 6 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                    borderRadius: 3,
                    bgcolor: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.07)",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Skeleton variant="rounded" width={34} height={34} sx={{ bgcolor: "rgba(255,255,255,0.1)", borderRadius: 2 }} />
                    <Box>
                      <Skeleton variant="text" width={160} sx={{ bgcolor: "rgba(255,255,255,0.15)" }} />
                      <Skeleton variant="text" width={80} sx={{ bgcolor: "rgba(255,255,255,0.1)" }} />
                    </Box>
                  </Box>
                  <Skeleton variant="text" width={60} sx={{ bgcolor: "rgba(255,255,255,0.1)" }} />
                </Box>
              </Grid>
            ))
          ) : recentActivity.length === 0 ? (
            <Grid size={12}>
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>
                  <LocalText>No recent activity found.</LocalText>
                </Typography>
              </Box>
            </Grid>
          ) : (
            recentActivity.map((item, index) => {
              const { Icon: ActionIcon, color: iconColor, bg: iconBg } = getActionMeta(item.action);
              return (
                <Grid key={index} size={{ xs: 12, md: 6 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 2,
                      borderRadius: 3,
                      bgcolor: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.07)",
                      height: "100%",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.10)",
                        borderColor: "rgba(255,255,255,0.16)",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
                      <Box sx={{ p: 0.8, borderRadius: 2, flexShrink: 0, bgcolor: iconBg }}>
                        <ActionIcon sx={{ fontSize: 18, color: iconColor, display: "block" }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        {/* Entity name + type */}
                        <Box sx={{ pl: 1.5, borderLeft: "2px solid rgba(255,255,255,0.18)", mb: 0.6 }}>
                          <Typography variant="body2" fontWeight={600} color="white" noWrap>
                            {item.entityName
                              ? <LocalText>{item.entityName}</LocalText>
                              : `#${item.entityId ?? "?"}`}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: "block", mt: 0.3, color: "#9cb6d9", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, fontSize: 10, opacity: 0.85 }}
                            noWrap
                          >
                            {item.entityType ? <LocalText>{item.entityType}</LocalText> : "—"}
                          </Typography>
                        </Box>
                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.40)", fontSize: 11, textTransform: "capitalize" }} noWrap>
                          {item.action ? <LocalText>{item.action}</LocalText> : "—"}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(255,255,255,0.4)", fontWeight: 500, flexShrink: 0, ml: 2, whiteSpace: "nowrap" }}
                    >
                      {item.performedAt ? new Date(item.performedAt).toLocaleDateString() : "—"}
                    </Typography>
                  </Box>
                </Grid>
              );
            })
          )}
        </Grid>
      </Card>}

      {false && !isSystemAdmin && (
        <ActivityLogDialog
          open={activityDialogOpen}
          onClose={() => setActivityDialogOpen(false)}
        />
      )}
    </Box>
  );
};

export default Dashboard;
